import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { mockAppointments, mockCandidates } from '../data/mockData';
import { subscribeToTasks, updateTask as updateTaskInFirebase, Task as FirebaseTask } from '../services/taskService';
import { cn } from '../utils';
import { navigateWithAction, NavigationActions } from '../utils/navigationUtils';
// Removed Firebase service imports - using LocalStorage instead
import { LocalStorageManager } from '../utils/localStorage'; 
// STEP 1: Import the new real-time services
import { subscribeToCandidates, updateCandidate } from '../services/candidateService';
import { subscribeToJobs, updateJob } from '../services/jobService';
import { subscribeToAppointments, updateAppointment } from '../services/appointmentService';
import { useNotifications } from '../contexts/NotificationContext';
import { Job } from '../types';
import { StatusBadge, candidateStatusOptions, jobStatusOptions, taskStatusOptions } from '../components/ui/StatusBadge';
import { 
  Calendar, 
  Users, 
  Clock, 
  CheckSquare, 
  User,
  Plus,
  Search,
  Eye,
  ListChecks,
  CalendarDays,
  UserPlus,
  CalendarPlus,
  Mail,
  ArrowRight,
  Briefcase
} from 'lucide-react';

// Use Firebase Task interface for receptionist tasks
type Task = FirebaseTask;

const ALL_TASK_STATUSES: Task['status'][] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];

const ReceptionistViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { notifyBadgeChange } = useNotifications();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [allCandidates, setAllCandidates] = useState<any[]>([]);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [sortStatus, setSortStatus] = useState<'All' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE'>('All');
  const [isLoading, setIsLoading] = useState(false);

  // Load data from LocalStorage - ONLY actual data, no mock data mixing or slicing for counts
  useEffect(() => {
    setIsLoading(true);
    
    // Load appointments from LocalStorage - ONLY real data
    const localAppointments = LocalStorageManager.getAppointments();
    setAppointments(localAppointments);
    let unsubscribe: (() => void) | null = null;
    let unsubCandidates: (() => void) | null = null;
    let unsubJobs: (() => void) | null = null;
    let unsubAppointments: (() => void) | null = null;

    // Load candidates from LocalStorage - ONLY real data  
    const localCandidates = LocalStorageManager.getCandidates();
    setAllCandidates(localCandidates);

    // Load jobs from localStorage - ONLY real data
    const jobs = LocalStorageManager.getJobs();
    setAllJobs(jobs);
    
    // STEP 2: Subscribe to all data sources with real-time listeners
    const setupAllSubscriptions = async () => {
      try {
        // Subscribe to candidates
        unsubCandidates = await subscribeToCandidates((candidates) => {
          console.log('📞 Receptionist: Received', candidates.length, 'candidates from Firebase');
          setAllCandidates(candidates);
        }, console.error);
        
        // Subscribe to jobs
        unsubJobs = subscribeToJobs((jobs) => {
          console.log('📞 Receptionist: Received', jobs.length, 'jobs from Firebase');
          setAllJobs(jobs);
        });
        
        // Subscribe to appointments
        unsubAppointments = subscribeToAppointments((appointments) => {
          console.log('📞 Receptionist: Received', appointments.length, 'appointments from Firebase');
          setAppointments(appointments);
        });
      } catch (error) {
        console.error('📞 Receptionist: Failed to setup subscriptions:', error);
      }
    };
    
    setupAllSubscriptions();
    
    // Subscribe to real-time tasks updates from Firebase
    const setupTasksSubscription = async () => {
      try {
        console.log('📞 Receptionist: Setting up Firebase real-time listener for tasks...');
        unsubscribe = await subscribeToTasks((firebaseTasks) => {
          console.log('📞 Receptionist: Received', firebaseTasks.length, 'tasks from Firebase');
          setAllTasks(firebaseTasks); // Keep ALL tasks for accurate counts
        }, (error) => {
          console.error('📞 Receptionist: Firebase tasks subscription error:', error);
        });
      } catch (error) {
        console.error('📞 Receptionist: Failed to setup tasks subscription:', error);
      }
    };
    
    setupTasksSubscription();
    
    // Comprehensive data refresh function
    const refreshAllData = () => {
      console.log('📞 Receptionist: Refreshing all data...');
      const localAppointments = LocalStorageManager.getAppointments();
      const localCandidates = LocalStorageManager.getCandidates();
      const jobs = LocalStorageManager.getJobs();
      
      setAppointments(localAppointments);
      setAllCandidates(localCandidates);
      setAllJobs(jobs);
      
      console.log('📞 Receptionist: Data refreshed - Candidates:', localCandidates.length, 'Jobs:', jobs.length, 'Appointments:', localAppointments.length);
    };

    // Listen for localStorage changes to keep data synced
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith('recruitment_crm_')) {
        console.log('📞 Receptionist: LocalStorageManager data changed for key:', e.key);
        refreshAllData();
      }
    };

    // Listen for custom events for instant sync
    const handleDataUpdates = (event: CustomEvent) => {
      console.log('📞 Receptionist: Data update event received:', event.type);
      refreshAllData();
    };
    
    window.addEventListener('storage', handleStorageChange);
    // Add comprehensive event listeners for instant updates
    window.addEventListener('candidatesUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateAdded', handleDataUpdates as EventListener);
    window.addEventListener('candidateUpdated', handleDataUpdates as EventListener);
    window.addEventListener('candidateDeleted', handleDataUpdates as EventListener);
    window.addEventListener('jobsUpdated', handleDataUpdates as EventListener);
    window.addEventListener('jobAdded', handleDataUpdates as EventListener);
    window.addEventListener('jobUpdated', handleDataUpdates as EventListener);
    window.addEventListener('jobDeleted', handleDataUpdates as EventListener);
    window.addEventListener('appointmentsUpdated', handleDataUpdates as EventListener);
    window.addEventListener('appointmentAdded', handleDataUpdates as EventListener);
    window.addEventListener('appointmentUpdated', handleDataUpdates as EventListener);
    window.addEventListener('appointmentDeleted', handleDataUpdates as EventListener);
    setIsLoading(false);
    
    return () => {
      console.log('📞 Receptionist: Cleaning up all Firebase subscriptions and event listeners...');
      if (unsubscribe) {
        unsubscribe();
      }
      if (unsubCandidates) {
        unsubCandidates();
      }
      if (unsubJobs) {
        unsubJobs();
      }
      if (unsubAppointments) {
        unsubAppointments();
      }
      window.removeEventListener('storage', handleStorageChange);
      // Remove all data update event listeners
      window.removeEventListener('candidatesUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateAdded', handleDataUpdates as EventListener);
      window.removeEventListener('candidateUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('candidateDeleted', handleDataUpdates as EventListener);
      window.removeEventListener('jobsUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('jobAdded', handleDataUpdates as EventListener);
      window.removeEventListener('jobUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('jobDeleted', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentsUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentAdded', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentUpdated', handleDataUpdates as EventListener);
      window.removeEventListener('appointmentDeleted', handleDataUpdates as EventListener);
      // STEP 3: Unsubscribe from all listeners on cleanup
      unsubscribe?.();
      unsubCandidates?.();
      unsubJobs?.();
      unsubAppointments?.();
    };
  }, []);

  // Listen for status changes from other pages
  useEffect(() => {
    const handleCandidateStatusUpdate = (event: CustomEvent) => {
      const { id, newStatus } = event.detail;
      setAllCandidates(prev => prev.map(c => {
        const cId = c._id || (c as any).id;
        return (cId === id) ? { ...c, stage: newStatus } : c;
      }));
    };

    const handleJobStatusUpdate = (event: CustomEvent) => {
      const { id, newStatus } = event.detail;
      setAllJobs(prev => prev.map(j => 
        j.id === id ? { ...j, status: newStatus } : j
      ));
    };

    // Add event listeners
    window.addEventListener('candidateStatusChanged', handleCandidateStatusUpdate as EventListener);
    window.addEventListener('jobStatusChanged', handleJobStatusUpdate as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('candidateStatusChanged', handleCandidateStatusUpdate as EventListener);
      window.removeEventListener('jobStatusChanged', handleJobStatusUpdate as EventListener);
    };
  }, []);

  // Helper functions
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEmailRedirect = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('outlook') || userAgent.includes('office')) {
      window.open('mailto:', '_blank');
    } else {
      const choice = window.prompt('Choose your email provider:\n1. Gmail\n2. Outlook\n\nEnter 1 or 2:');
      if (choice === '1') {
        window.open('https://mail.google.com/mail/u/0/#inbox?compose=new', '_blank');
      } else if (choice === '2') {
        window.open('https://outlook.live.com/mail/0/inbox', '_blank');
      } else {
        window.open('mailto:', '_blank');
      }
    }
  };
  
  const displayedTasks = useMemo(() => {
    let filtered = allTasks;
    if (sortStatus !== 'All') {
      filtered = filtered.filter(task => task.status === sortStatus);
    }
    return filtered.slice(0, 10); // Limit to 10 tasks
  }, [allTasks, sortStatus]);

  const displayedCandidates = useMemo(() => {
    return allCandidates.slice(0, 10).map(candidate => ({
      id: candidate._id || (candidate as any).id, // Handle both _id and id fields
      name: candidate.name,
      stage: candidate.stage,
      jobTitle: candidate.jobTitle || 'N/A',
      country: candidate.countryApplyingTo || 'N/A'
    }));
  }, [allCandidates]);
  
  const getStatusDisplayName = (status: Task['status']): string => {
    if (status === 'COMPLETED') return 'Done';
    return status.replace('_',' ');
  };

  const handleTaskStatusChange = (taskId: string, newStatus: string) => {
    const mappedStatus = newStatus.toUpperCase().replace('-', '_') as Task['status'];
    const task = allTasks.find(t => t.id === taskId);
    if (!task) return;

    const oldStatus = task.status;
    const taskTitle = task.title || 'Unknown Task';

    // Update Firebase - the real-time listener will update the UI
    updateTaskInFirebase(taskId, { status: mappedStatus })
      .then(() => {
        console.log(`🏢 Receptionist: Successfully updated task status: ${oldStatus} → ${mappedStatus}`);
        // Notify badge change
        notifyBadgeChange('Receptionist View', `${taskTitle} Status`, oldStatus, mappedStatus);
      })
      .catch(error => {
        console.error('❌ Receptionist: Failed to update task status in Firebase:', error);
      });
  };

  const handleCandidateStatusChange = (candidateId: string, newStatus: string) => {
    const candidate = allCandidates.find(c => (c._id === candidateId) || ((c as any).id === candidateId));
    if (!candidate) {
      console.error('❌ Candidate not found:', candidateId);
      return;
    }

    const oldStatus = candidate.stage;
    const actualId = candidate._id || (candidate as any).id;
    
    LocalStorageManager.updateCandidate(actualId, { stage: newStatus as any });
    
    setAllCandidates(prev => prev.map(c => {
      const cId = c._id || (c as any).id;
      return (cId === candidateId) ? { ...c, stage: newStatus } : c;
    }));

    notifyBadgeChange('Receptionist View', `${candidate.name} Status`, oldStatus, newStatus);
    
    window.dispatchEvent(new CustomEvent('candidateStatusChanged', { 
      detail: { id: actualId, oldStatus, newStatus, candidateName: candidate.name }
    }));
    
    // Dispatch comprehensive update events for instant sync across all pages
    window.dispatchEvent(new CustomEvent('candidateUpdated', { 
      detail: { id: actualId, oldStatus, newStatus, candidateName: candidate.name }
    }));
    window.dispatchEvent(new CustomEvent('candidatesUpdated', { 
      detail: { action: 'statusChanged', candidateId: actualId, candidateName: candidate.name }
    }));

    console.log(`📊 Updated candidate ${candidate.name} status: ${oldStatus} → ${newStatus}`);
    // STEP 4: Use the new service to update Firestore
    updateCandidate(actualId, { stage: newStatus as any })
      .then(() => {
        notifyBadgeChange('Receptionist View', `${candidate.name} Status`, oldStatus, newStatus);
        console.log(`📊 Updated candidate ${candidate.name} status: ${oldStatus} → ${newStatus}`);
      })
      .catch(error => {
        console.error('❌ Failed to update candidate status:', error);
        // Optionally, revert the UI change or show an error message
      });
  };

  const handleJobStatusChange = (jobId: string, newStatus: string) => {
    const job = allJobs.find(j => j.id === jobId);
    if (!job) return;

    const oldStatus = job.status;
    
    LocalStorageManager.updateJob(jobId, { status: newStatus as any });
    
    setAllJobs(prev => prev.map(j => 
      j.id === jobId ? { ...j, status: newStatus as any } : j
    ));

    notifyBadgeChange('Receptionist View', `${job.title} Status`, oldStatus, newStatus);
    
    window.dispatchEvent(new CustomEvent('jobStatusChanged', { 
      detail: { id: jobId, oldStatus, newStatus, jobTitle: job.title }
    }));
    
    // Dispatch comprehensive update events for instant sync across all pages
    window.dispatchEvent(new CustomEvent('jobUpdated', { 
      detail: { id: jobId, oldStatus, newStatus, jobTitle: job.title }
    }));
    window.dispatchEvent(new CustomEvent('jobsUpdated', { 
      detail: { action: 'statusChanged', jobId: jobId, jobTitle: job.title }
    }));

    console.log(`📊 Updated job ${job.title} status: ${oldStatus} → ${newStatus}`);
    // STEP 5: Use the new service to update Firestore
    updateJob(jobId, { status: newStatus as any })
      .then(() => {
        notifyBadgeChange('Receptionist View', `${job.title} Status`, oldStatus, newStatus);
        console.log(`📊 Updated job ${job.title} status: ${oldStatus} → ${newStatus}`);
      })
      .catch(error => {
        console.error('❌ Failed to update job status:', error);
      });
  };
  
  // Live data counts for receptionist stats - synced like Reports page
  const receptionistStats = [
    { title: "Total Candidates", value: allCandidates.length.toString(), icon: Users, trend: "Live data" },
    { title: "Total Jobs", value: allJobs.length.toString(), icon: Briefcase, trend: "Live data" },
    { title: "Total Appointments", value: appointments.length.toString(), icon: CalendarDays, trend: "Live data" },
    { title: "Pending Tasks", value: displayedTasks.filter(t => t.status === 'PENDING').length.toString(), icon: CheckSquare, trend: "Live data" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>
          Receptionist View
        </h1>
        <p className="text-gray-600 mt-1">
          Manage daily reception activities and candidate interactions
        </p>
      </div>

      {/* Office Resources Section */}
      <div className={`${recruitmentTheme.components.card} bg-card shadow-md`}>
        <div className="p-4">
          <h2 className={`${themeClasses.sectionTitle} mb-4`}>Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <button 
              onClick={() => navigate('/cv-builder')}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <User className="mr-2 h-4 w-4" /> Create CV
            </button>
            <button 
              onClick={() => navigateWithAction(navigate, NavigationActions.addCandidate)}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <UserPlus className="mr-2 h-4 w-4" /> Add Candidate
            </button>
            <button 
              onClick={() => navigateWithAction(navigate, NavigationActions.searchCandidates)}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <Search className="mr-2 h-4 w-4" /> View Candidate
            </button>
            <button 
              onClick={() => navigateWithAction(navigate, NavigationActions.scheduleAppointment)}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <CalendarPlus className="mr-2 h-4 w-4" /> Schedule Appointment
            </button>
            <button 
              onClick={() => navigate('/tasks')}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <Eye className="mr-2 h-4 w-4" /> View Tasks
            </button>
            <button 
              onClick={handleEmailRedirect}
              className={`${recruitmentTheme.components.button.secondary} w-full justify-start`}
            >
              <Mail className="mr-2 h-4 w-4" /> Send Email
            </button>
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {receptionistStats.map(stat => (
          <div key={stat.title} className={`${recruitmentTheme.components.card} shadow-md hover:shadow-lg transition-shadow`}>
            <div className="flex flex-row items-center justify-between space-y-0 pb-2 p-4">
              <div className="text-sm font-medium text-[#8b6914]">{stat.title}</div>
              <stat.icon className="h-5 w-5 text-[#8b6914]" />
            </div>
            <div className="px-4 pb-4">
              <div className="text-2xl font-bold text-[#8b6914]">{stat.value}</div>
              <p className="text-xs text-gray-600">{stat.trend}</p>
            </div>
          </div>
        ))}
      </div>
      
      {/* Recent Candidates - Moved to Top */}

      <div className={recruitmentTheme.components.card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-[#8b6914]" />
            <h2 className={themeClasses.sectionTitle}>Recent Candidates</h2>
            <span className="text-sm text-gray-500">({displayedCandidates.length})</span>
          </div>
          <button 
            onClick={() => navigate('/candidates')}
            className={`${recruitmentTheme.components.button.ghost} flex items-center space-x-1 text-sm`}
          >
            <span>View All Candidates</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          {displayedCandidates.slice(0, 5).map((candidate, index) => (
            <div key={candidate.id} className="p-3 bg-cream-warm rounded-lg border border-gray-200 hover:bg-[#f0e4cc]/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                    {index + 1}.
                  </span>
                  <div className="w-8 h-8 bg-[#b8860b] rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {candidate.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[#8b6914] text-sm">{candidate.name}</p>
                    <p className="text-xs text-gray-600">{candidate.jobTitle} • {allCandidates.find(c => (c._id === candidate.id) || ((c as any).id === candidate.id))?.phone} • {candidate.country}</p>
                    <p className="text-xs text-gray-500">Passport: {allCandidates.find(c => (c._id === candidate.id) || ((c as any).id === candidate.id))?.passportNumber || 'N/A'} • Salary: KES {allCandidates.find(c => (c._id === candidate.id) || ((c as any).id === candidate.id))?.salary?.toLocaleString() || 'N/A'}</p>
                  </div>
                </div>
                <StatusBadge
                  currentStatus={candidate.stage}
                  options={candidateStatusOptions}
                  onStatusChange={(newStatus) => handleCandidateStatusChange(candidate.id, newStatus)}
                  size="sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Jobs */}
      <div className={recruitmentTheme.components.card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Briefcase className="w-5 h-5 text-[#8b6914]" />
            <h2 className={themeClasses.sectionTitle}>Recent Jobs</h2>
            <span className="text-sm text-gray-500">({allJobs.length})</span>
          </div>
          <button 
            onClick={() => navigate('/jobs')}
            className={`${recruitmentTheme.components.button.ghost} flex items-center space-x-1 text-sm`}
          >
            <span>View All Jobs</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          {allJobs.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">Add your first job posting to get started.</p>
            </div>
          ) : (
            allJobs.slice(0, 5).map((job, index) => (
              <div key={job.id} className="p-3 bg-cream-warm rounded-lg border border-gray-200 hover:bg-[#f0e4cc]/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                      {index + 1}.
                    </span>
                    <div className="w-8 h-8 bg-[#8b6914] rounded-full flex items-center justify-center text-white text-sm font-medium">
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-[#8b6914] text-sm">{job.title}</p>
                      <p className="text-xs text-gray-600">{job.company} • {job.location} • {job.type}</p>
                      <p className="text-xs text-gray-500">{job.salaryRange?.min || 'N/A'} - {job.salaryRange?.max || 'N/A'} {job.salaryRange?.currency || 'KES'} • Posted {formatDate(job.postedDate)}</p>
                    </div>
                  </div>
                  <StatusBadge
                    currentStatus={job.status.toLowerCase()}
                    options={jobStatusOptions}
                    onStatusChange={(newStatus) => handleJobStatusChange(job.id, newStatus.charAt(0).toUpperCase() + newStatus.slice(1))}
                    size="sm"
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* My Task Progress Section - Moved Below Recent Candidates */}
      <div className={recruitmentTheme.components.card}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-6 p-4 pb-0">
          <div className="mb-2 sm:mb-0">
            <h2 className={`${themeClasses.sectionTitle} flex items-center gap-2 text-2xl`}>
              <ListChecks className="h-6 w-6 text-[#8b6914]" /> My Task Progress
            </h2>
            <p className="text-sm text-gray-600">Summary of tasks assigned to you. Status can be changed directly.</p>
          </div>
          <select
            value={sortStatus}
            onChange={(e) => setSortStatus(e.target.value as 'All' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE')}
            className={`${recruitmentTheme.components.select} w-full sm:w-[200px]`}
          >
            <option value="All">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Done (Completed)</option>
            <option value="OVERDUE">Overdue</option>
          </select>
        </div>
        <div className="px-2 pt-0 pb-6">
          {displayedTasks.length > 0 ? (
            <ul className="space-y-4">
              {displayedTasks.map((task, index) => (
                <li key={task.id} className="p-4 bg-card rounded-md shadow-md">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-grow mr-2">
                      <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                        {index + 1}.
                      </span>
                      <div className="flex flex-col flex-grow">
                        <h3 className="text-lg font-semibold">{task.title}</h3>
                        {(task as any).candidateName && (
                          <p className="text-sm text-gray-600">Candidate: {(task as any).candidateName}</p>
                        )}
                        <p className="text-sm text-gray-600">Assigned To: {task.assignedTo}</p>
                        {(task as any).notes && (
                          <p className="text-sm text-gray-600 italic">Note: {(task as any).notes}</p>
                        )}
                      </div>
                    </div>
                    <StatusBadge
                      currentStatus={task.status.toLowerCase().replace('_', '-')}
                      options={taskStatusOptions}
                      onStatusChange={(newStatus) => handleTaskStatusChange(task.id!, newStatus)}
                      size="sm"
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-6">
              {sortStatus === 'All' ? 'No tasks assigned to you currently.' : `No tasks with status "${getStatusDisplayName(sortStatus as Task['status'])}" assigned to you.`}
            </p>
          )}
        </div>
      </div>

    </div>
  );
};

export default ReceptionistViewPage;

// Define ReportData interface
interface ReportData {
  totalCandidates: number;
  candidatesByStage: Record<string, number>;
  activeJobs: number;
  todaysAppointments: number;
  pendingTasks: number;
}
